/**
 * buildReviewPrompt — pure function that assembles the Claude prompt for
 * full generation, single-field regeneration, and field refinement.
 * Extracted from AIReviewTab to keep file sizes within the 500-line limit.
 */
import {
  FINN_VALUES, BEHAVIORS, getBehaviorGroup, getRatingLabel, RATING_SCALE,
} from '../../data/managerReviewData';
import type { Employee, BehaviorData, Deliverable, FeedbackItem } from '../../types/managerReview';

export interface BuildPromptData {
  employee: Employee;
  level: string;
  behaviorsData: Record<string, BehaviorData>;
  behaviorsRating: number | null;
  behaviorsRatingNote: string;
  deliverables: Deliverable[];
  deliveryRating: number | null;
  overallRating: number | null;
  overallRatingNote: string;
  promotion: 'yes' | 'no' | null;
  promotionNote: string;
  biasNote: string;
  selfReview: string;
  feedbackItems: FeedbackItem[];
  /** Raw multi-reviewer performance-review feedback paste */
  perfFeedbackRaw?: string;
  /** Raw multi-reviewer anytime feedback paste */
  anytimeFeedbackRaw?: string;
}

export function buildReviewPrompt(
  data: BuildPromptData,
  mode: 'full' | 'delivery' | 'behaviors' | 'refine-delivery' | 'refine-behaviors' = 'full',
  refineOpts?: { currentText: string; instruction: string },
): string {
  const {
    employee, level, behaviorsData, behaviorsRating, behaviorsRatingNote,
    deliverables, deliveryRating, overallRating, overallRatingNote,
    promotion, promotionNote, biasNote, selfReview, feedbackItems,
    perfFeedbackRaw, anytimeFeedbackRaw,
  } = data;

  const rl = (r: number | null) => r ? `${r}/5 — ${getRatingLabel(r)}` : 'not yet rated';

  // Compute suggested ratings (mirrors SummaryTab / BehaviorsTab logic)
  // Used to detect whether deviation-note fields are actually visible in the UI
  const ratedValues = FINN_VALUES.filter(v => behaviorsData[v.name]?.rating != null);
  const avgBehaviors = ratedValues.length > 0
    ? ratedValues.reduce((sum, v) => sum + (behaviorsData[v.name].rating ?? 0), 0) / ratedValues.length
    : null;
  const suggestedBehaviorsRating = avgBehaviors !== null ? Math.round(avgBehaviors) : null;
  const overallCalc = behaviorsRating && deliveryRating ? (behaviorsRating + deliveryRating) / 2 : null;
  const isTieCalc = overallCalc !== null && (overallCalc * 10) % 5 === 0 && !Number.isInteger(overallCalc);
  const suggestedOverallRating = isTieCalc ? deliveryRating : (overallCalc ? Math.round(overallCalc) : null);

  // Only include deviation notes when the note field is actually visible in the UI
  const activeOverallRatingNote = (overallRating && suggestedOverallRating && overallRating !== suggestedOverallRating)
    ? overallRatingNote : '';
  const activeBehaviorsRatingNote = (behaviorsRating && suggestedBehaviorsRating && behaviorsRating !== suggestedBehaviorsRating)
    ? behaviorsRatingNote : '';
  const activePromotionNote = promotion === 'yes' ? promotionNote : '';

  const filledDeliverablesCnt = deliverables.filter(d => d.title?.trim() || d.rating);
  const dCount = filledDeliverablesCnt.length;

  // Content-density depth hint — based on average note length, not deliverable count
  const totalNoteChars = filledDeliverablesCnt.reduce((sum, d) =>
    sum + (d.wellDone?.trim().length ?? 0) + (d.improvement?.trim().length ?? 0), 0);
  const avgNoteChars = dCount > 0 ? totalNoteChars / dCount : 0;
  const depthHint = avgNoteChars > 300
    ? 'draw fully on the manager notes — depth is warranted, aim for 4–6 sentences per sub-section'
    : avgNoteChars > 120
    ? '2–4 sentences per sub-section — match depth to available notes'
    : '1–2 sentences per sub-section — notes are sparse, do not pad with generic statements';

  const scaleText = RATING_SCALE.map(r => `${r.v} — ${r.label}: ${r.desc[0]}`).join('\n');

  const bGroup = getBehaviorGroup(level);
  const frameworkText = FINN_VALUES.map(v => {
    const bullets = BEHAVIORS[bGroup]?.[v.name] || [];
    return `${v.name} (${v.sub}):\n${bullets.map(b => `  • ${b.replace(/\*\*/g, '')}`).join('\n')}`;
  }).join('\n\n');

  const filledDeliverables = filledDeliverablesCnt;
  const dBlock = filledDeliverables.map((d, i) => {
    const lines = [
      `D${i + 1}: "${d.title?.trim() || 'Untitled'}" — Rating: ${rl(d.rating)}`,
      d.competencies?.length ? `  Competencies demonstrated: ${d.competencies.join(', ')}` : null,
      d.wellDone?.trim() ? `  Manager note — what went well: ${d.wellDone.trim()}` : '  Manager note — what went well: (nothing entered)',
      d.improvement?.trim() ? `  Manager note — what to improve: ${d.improvement.trim()}` : '  Manager note — what to improve: (nothing entered)',
    ];
    return lines.filter(Boolean).join('\n');
  }).join('\n\n');

  const bBlock = FINN_VALUES.map(v => {
    const d = behaviorsData[v.name];
    const obs = d?.observed || {};
    const bullets = BEHAVIORS[bGroup]?.[v.name] || [];
    const yes = bullets.filter(b => obs[b] === true).map(b => `  ✓ ${b.replace(/\*\*/g, '')}`);
    const no  = bullets.filter(b => obs[b] === false).map(b => `  ✗ ${b.replace(/\*\*/g, '')}`);
    const notAnswered = bullets.filter(b => obs[b] === undefined).length;
    const strengths = d?.strengthsText?.trim() ? [d.strengthsText.trim()] : [];
    const improvements = d?.improvementsText?.trim() ? [d.improvementsText.trim()] : [];
    return [
      `${v.name}: ${rl(d?.rating ?? null)}`,
      yes.length ? yes.join('\n') : null,
      no.length ? no.join('\n') : null,
      notAnswered ? `  (${notAnswered} checklist item(s) left unanswered)` : null,
      strengths.length ? `  Manager notes — strengths:\n${strengths.map(s => `    + ${s}`).join('\n')}` : '  Manager notes — strengths: (nothing entered)',
      improvements.length ? `  Manager notes — growth areas:\n${improvements.map(s => `    - ${s}`).join('\n')}` : '  Manager notes — growth areas: (nothing entered)',
    ].filter(Boolean).join('\n');
  }).join('\n\n');

  const feedbackBlock = [
    selfReview?.trim() ? `Employee self-review:\n${selfReview.trim()}` : null,
    perfFeedbackRaw?.trim() ? `Peer & Upward Feedback:\n${perfFeedbackRaw.trim()}` : null,
    anytimeFeedbackRaw?.trim() ? `Anytime feedback:\n${anytimeFeedbackRaw.trim()}` : null,
    // Legacy individual feedback items (kept for backwards compatibility)
    feedbackItems?.filter(f => f.text?.trim()).map(f => `${f.type} feedback:\n"${f.text.trim()}"`).join('\n\n') || null,
  ].filter(Boolean).join('\n\n');

  const biasBlock = biasNote?.trim() ? `Manager self-reflection / potential blind spots:\n"${biasNote.trim()}"` : null;

  const contextBlock = `You are an expert HR coach helping a manager write a calibration-ready performance review for FINN (a car subscription company).

The review is addressed directly TO the employee: ${employee.name} (${employee.role}, Level: ${level}).

══ GROUNDING RULE — CRITICAL ══════════════════════════════════
Draw only from:
  1. The MANAGER INPUT provided (primary source for all review prose)
  2. The RATING SCALE definitions (to contextualise ratings)
  3. The TALENT FRAMEWORK definitions (reference context only — not to be used as a substitute for missing manager input)

If manager input is absent for a topic (no text notes — only a rating or checklist ticks):
  → Write the literal string: [INSUFFICIENT DATA — add specific examples to make this section meaningful for the employee]
  → Do NOT complete, replace, or work around this placeholder yourself
  → Do NOT use talent framework language to fill in where real manager input is missing
  → Do NOT use multi-source feedback (peer, upward, self-review, anytime) as a substitute for missing manager notes — peer/anytime feedback is context only; specific behavioral examples in the generated text must be anchored in the manager's own notes
NEVER invent events, achievements, examples, or outcomes.
═══════════════════════════════════════════════════════════════

══ TONE & STYLE ════════════════════════════════════════════════
• Address the employee in SECOND PERSON throughout — "you delivered", "your approach", "you demonstrated"
• Language must be inclusive and bias-free: avoid personality labels and vague praise. Do NOT flag gendered pronouns in the manager's raw notes — the output is always in second person ("you"), so pronoun choice in the input is irrelevant.
• Tone: personal yet professional; direct but sensitive
• For positive feedback: be specific and appreciative — name exactly what was done well and why it mattered
• For constructive feedback: be clear about the gap, but frame it as a forward-looking growth opportunity
• Never use hollow filler phrases like "great communicator", "strong team player", or "very dedicated"
• Calibrate the strength/development balance per individual rating:
    1 – Not meeting:     ~15% strengths / ~85% development  |  language: corrective
    2 – Partially:       ~30% strengths / ~70% development  |  language: honest
    3 – Fully meeting:   ~55% strengths / ~45% development  |  language: directional
    4 – Exceeding:       ~70% strengths / ~30% development  |  language: aspirational
    5 – Outperforming:   ~80% strengths / ~20% development  |  language: ceiling-breaking — never corrective
• PROMOTION CONTEXT: CRITICAL: NEVER mention, hint at, or reference the promotion decision anywhere in the review text.
• COMPENSATION CONTEXT: CRITICAL: NEVER mention, hint at, or reference any compensation outcome, salary adjustment, merit increase, or key contributor bonus anywhere in the review text.
═══════════════════════════════════════════════════════════════

RATING SCALE:
${scaleText}

TALENT FRAMEWORK — ${level} Behavior Definitions:
${frameworkText}

══ MANAGER INPUT ════════════════════════════════════════════
EMPLOYEE CONTEXT:
  Previously promoted last cycle: ${employee.previouslyPromoted ? 'Yes' : 'No'}

OVERALL RATING: ${rl(overallRating)}${activeOverallRatingNote?.trim() ? `\n  Manager's note on overall rating (deviation from suggestion): "${activeOverallRatingNote.trim()}"` : ''}
DELIVERY RATING: ${rl(deliveryRating)}

DELIVERABLES:
${dBlock || '(no deliverables entered)'}

OVERALL BEHAVIORS RATING: ${rl(behaviorsRating)}${activeBehaviorsRatingNote?.trim() ? `\n  Manager's note on behaviors rating (deviation from suggestion): "${activeBehaviorsRatingNote.trim()}"` : ''}

BEHAVIORS:
${bBlock}
${feedbackBlock ? `\nMULTI-SOURCE FEEDBACK:\n${feedbackBlock}` : ''}
${biasBlock ? `\n${biasBlock}` : ''}
PROMOTION DECISION: ${promotion === 'yes' ? 'Yes — nominated for promotion' : promotion === 'no' ? 'No — not nominated' : 'Not yet decided'}${activePromotionNote?.trim() ? `\n  Manager's reasoning (context for tone calibration ONLY — NEVER reference in review text): "${activePromotionNote.trim()}"` : ''}
═══════════════════════════════════════════════════════════════`;

  const field3Block = `### FIELD 3: KEY DELIVERABLES
Write in second person, addressed directly to the employee. Use this exact structure:

1. Opening statement — one sentence summarising the overall pattern or theme.

2. For EACH deliverable, a titled block (${depthHint}):
   **[Deliverable title]**
   **Achieved Impact:** [what you accomplished and why it mattered]
   **Demonstrated Strengths:** [specific behaviours shown, referencing tagged competencies]
   **Opportunities for Improvement:** [where you could have gone further — forward-looking]

3. Closing statement — one sentence connecting delivery to the overall performance picture.

If manager notes are absent for a deliverable (no wellDone / improvement text), write [INSUFFICIENT DATA — add specific examples to make this section meaningful]. Do NOT pad with framework language, generic statements, or inferred observations.`;

  const field5Block = `### FIELD 5: BEHAVIORS
Write in second person, addressed directly to the employee. Cover all 5 FINN values in order.
Open each value section with its name as a bold heading — e.g. **Aim High**.
Structure each value section with two labeled sub-sections:
  **Strengths:** [1–3 sentences — what the employee did well, grounded in specific observed behaviors and their impact]
  **Opportunities for growth:** [1–2 sentences — forward-looking development areas, framed constructively]
Match depth to available manager input — do not pad sparse notes.
Draw on the SBIR model as a guide (Situation, Behaviour, Impact, Request) — write as natural prose, not as a rigid template.
Use ✓ observed bullets as anchors for Strengths; ✗ not-observed bullets for Opportunities for growth.
If a value has no manager notes (only a rating, checklist ticks, or nothing at all), write [INSUFFICIENT DATA — add specific examples] for the entire value block. Do not complete this placeholder yourself.
Inclusive language throughout. No sub-ratings. No filler. Always write in English.`;

  if (mode === 'delivery') {
    return `${contextBlock}\n\nRegenerate ONLY the Key Deliverables field. Begin your response with the marker ### FIELD 3: and output nothing else.\n\n${field3Block}`;
  }

  if (mode === 'behaviors') {
    return `${contextBlock}\n\nRegenerate ONLY the Behaviors field. Begin your response with the marker ### FIELD 5: and output nothing else.\n\n${field5Block}`;
  }

  if ((mode === 'refine-delivery' || mode === 'refine-behaviors') && refineOpts) {
    const fieldLabel = mode === 'refine-delivery' ? 'Key Deliverables (Field 3)' : 'Behaviors (Field 5)';
    return `${contextBlock}

You are refining the ${fieldLabel} section based on the manager's instruction.

══ CURRENT FIELD TEXT ═══════════════════════════════════════════
${refineOpts.currentText}
═══════════════════════════════════════════════════════════════

══ MANAGER'S REFINEMENT INSTRUCTION ══════════════════════════
"${refineOpts.instruction}"
═══════════════════════════════════════════════════════════════

Apply the instruction. Rules:
1. Stay strictly within the grounding rule — do not invent facts not present in the manager input
2. Maintain second person and all tone requirements
3. Never reference or hint at the promotion decision or any compensation outcome (merit increase, key contributor bonus, salary adjustment)
4. If the instruction violates these rules, respond with EXACTLY:
   RULES_VIOLATION: [one sentence explaining which rule is violated]
5. Otherwise output the refined field text directly — no preamble, no field markers`;
  }

  return `${contextBlock}

Return EXACTLY two sections with these headers:

## SECTION 1: REVIEW QUALITY

List each issue on a new line using EXACTLY this format (no brackets, no other punctuation):
TIER CATEGORY — Action sentence (max 12 words). → Location

TIER is either BLOCKER or SUGGESTION:
- BLOCKER: INCONSISTENCY, BIAS, INCOMPLETE — things that will be challenged in calibration or make the review factually wrong
  Special rule: if an ENTIRE FINN value section has no manager input at all (no rating, no strengths text, no improvements text, no ✓/✗ observations), flag as BLOCKER INCOMPLETE — a whole missing value cannot produce meaningful review content.
- SUGGESTION: MISSING EVIDENCE, LACK OF CONTENT, VAGUE LANGUAGE — things that improve quality but manager can decide on

CATEGORY is one of: INCONSISTENCY, BIAS, INCOMPLETE, MISSING EVIDENCE, LACK OF CONTENT, VAGUE LANGUAGE

HARD RULE — VAGUE LANGUAGE only applies when a manager note is essentially content-free (e.g. "did well", "could improve", "good"). Do NOT flag notes that are specific but awkwardly phrased — the review generator will rephrase raw notes into polished prose automatically.

HARD RULE — Do NOT flag VAGUE LANGUAGE on "Manager's note on overall rating (deviation from suggestion)" or "Manager's note on behaviors rating (deviation from suggestion)" fields. These are brief administrative explanations for a rating deviation, not employee-facing content, and are never seen in calibration.

Action sentence: max 12 words, start with a verb, be specific (not "add more detail" — say exactly what to add).

Location (optional, include when issue maps to a specific place):
- "→ Delivery: [deliverable title]" for individual deliverable issues
- "→ Delivery" for overall delivery rating issues (the delivery rating dropdown is in the Delivery tab)
- "→ Behaviors: [value name]" for individual behavior value issues
- "→ Behaviors" for overall behaviors rating issues (the behaviors rating dropdown is in the Behaviors tab)
- "→ Summary" for overall performance rating or promotion decision issues
- "→ Before You Start" for feedback quality issues

HARD RULE — NEVER flag gendered pronouns (he/she/his/her/they) in manager notes as [BIAS] or any other category. The generated review output is always in second person ("you"), making pronoun choice in the manager's raw input completely irrelevant. If the only bias issue you would raise is pronoun usage, output no [BIAS] item at all.

If no issues: write only "No issues found."

## SECTION 2: CULTURE AMP — READY TO COPY

${field3Block}

${field5Block}`;
}
